<template>
  <el-card>
    <div class="toolbar">
      <el-input v-model="keyword" placeholder="标题关键词" clearable style="width: 220px" @keyup.enter.native="load" />
      <el-select v-model="statusFilter" clearable placeholder="状态" style="width: 120px" @change="onFilterChange">
        <el-option label="启用" :value="1" />
        <el-option label="禁用" :value="0" />
      </el-select>
      <el-button type="primary" @click="load">查询</el-button>
      <el-button type="success" @click="openCreate">新增文章</el-button>
    </div>
    <el-table :data="rows" border stripe v-loading="loading" style="width: 100%; margin-top: 12px">
      <el-table-column prop="title" label="标题" min-width="200" show-overflow-tooltip />
      <el-table-column label="封面" width="90">
        <template slot-scope="s">
          <el-image
            v-if="s.row.coverUrl"
            :src="resolveMediaUrl(s.row.coverUrl)"
            fit="cover"
            class="thumb"
            :preview-src-list="[resolveMediaUrl(s.row.coverUrl)]"
          />
          <span v-else class="cell-empty">—</span>
        </template>
      </el-table-column>
      <el-table-column prop="sortOrder" label="排序号" width="80" />
      <el-table-column label="状态" width="90">
        <template slot-scope="s">
          <el-tag :type="s.row.status === 1 ? 'success' : 'info'" size="mini">
            {{ s.row.status === 1 ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="创建时间" width="170">
        <template slot-scope="s">{{ formatTime(s.row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="220" fixed="right">
        <template slot-scope="s">
          <el-button type="text" @click="openEdit(s.row)">编辑</el-button>
          <el-button type="text" @click="toggleStatus(s.row)">{{ s.row.status === 1 ? '禁用' : '启用' }}</el-button>
          <el-button type="text" class="btn-danger" @click="remove(s.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      style="margin-top: 12px"
      background
      layout="total, prev, pager, next"
      :total="total"
      :page-size="size"
      :current-page.sync="page"
      @current-change="load"
    />

    <el-dialog
      :title="dlgTitle"
      :visible.sync="dlg"
      width="860px"
      top="6vh"
      :close-on-click-modal="false"
      @closed="onDialogClosed"
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入文章标题" maxlength="200" show-word-limit />
        </el-form-item>
        <el-form-item label="文章内容" prop="content">
          <div v-if="editorReady" class="rich-editor">
            <Toolbar :editor="editor" :defaultConfig="toolbarConfig" :mode="editorMode" class="rich-toolbar" />
            <Editor
              v-model="form.content"
              :defaultConfig="editorConfig"
              :mode="editorMode"
              class="rich-body"
              @onCreated="onEditorCreated"
            />
          </div>
          <p class="upload-hint">支持标题、加粗、列表、链接与插图；正文将以富文本展示在小程序文章详情页</p>
        </el-form-item>
        <el-form-item label="封面图" prop="coverUrl">
          <el-upload
            class="cover-uploader"
            :action="uploadAction"
            :headers="uploadHeaders"
            name="file"
            :data="{ folder: 'articles' }"
            :show-file-list="false"
            accept="image/*"
            :before-upload="beforeImageUpload"
            :on-success="onCoverSuccess"
          >
            <img v-if="form.coverUrl" :src="resolveMediaUrl(form.coverUrl)" class="cover-preview" />
            <i v-else class="el-icon-plus cover-uploader-icon" />
          </el-upload>
          <p class="upload-hint">必填，单张图片不超过 5MB</p>
        </el-form-item>
        <el-form-item label="上传视频">
          <el-upload
            :action="uploadAction"
            :headers="uploadHeaders"
            name="file"
            :data="{ folder: 'articles' }"
            accept="video/*"
            :before-upload="beforeVideoUpload"
            :on-success="onVideoSuccess"
            :on-remove="onVideoRemove"
            :file-list="videoFileList"
            :limit="1"
          >
            <el-button size="small" type="primary" plain>选择视频</el-button>
          </el-upload>
          <p class="upload-hint">选填，单个视频不超过 50MB；展示在小程序文章详情最下方</p>
        </el-form-item>
        <el-form-item label="排序号">
          <el-input-number v-model="form.sortOrder" :min="0" :max="99999" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="form.status" style="width: 120px">
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dlg = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import { Editor, Toolbar } from '@wangeditor/editor-for-vue';
import '@wangeditor/editor/dist/css/style.css';
import http from '../api/http';
import { resolveApiBaseUrl } from '../api/apiBase';

function isHtmlContentEmpty(html) {
  if (!html) return true;
  const text = String(html)
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, '')
    .trim();
  return !text;
}

export default {
  name: 'ArticlesView',
  components: { Editor, Toolbar },
  data() {
    const validateContent = (rule, value, callback) => {
      if (isHtmlContentEmpty(value)) {
        callback(new Error('请输入文章内容'));
      } else {
        callback();
      }
    };
    return {
      rows: [],
      keyword: '',
      statusFilter: null,
      page: 1,
      size: 20,
      total: 0,
      loading: false,
      dlg: false,
      dlgMode: 'create',
      saving: false,
      editor: null,
      editorReady: false,
      editorMode: 'default',
      toolbarConfig: {
        excludeKeys: ['fullScreen', 'group-video', 'insertVideo', 'uploadVideo']
      },
      editorConfig: {
        placeholder: '正文将展示在小程序文章详情页',
        MENU_CONF: {}
      },
      form: {
        id: null,
        title: '',
        content: '',
        coverUrl: '',
        videoUrl: '',
        sortOrder: 0,
        status: 1
      },
      rules: {
        title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
        content: [{ validator: validateContent, trigger: 'change' }],
        coverUrl: [{ required: true, message: '请上传封面图', trigger: 'change' }]
      }
    };
  },
  computed: {
    dlgTitle() {
      return this.dlgMode === 'create' ? '新增文章' : '编辑文章';
    },
    uploadAction() {
      return `${resolveApiBaseUrl()}/files/upload`;
    },
    uploadHeaders() {
      const t = localStorage.getItem('admin_token');
      return t ? { Authorization: `Bearer ${t}` } : {};
    },
    videoFileList() {
      if (!this.form.videoUrl) return [];
      const url = this.resolveMediaUrl(this.form.videoUrl);
      const name = this.form.videoUrl.split('/').pop() || 'video';
      return [{ name, url }];
    }
  },
  created() {
    const self = this;
    this.editorConfig.MENU_CONF.uploadImage = {
      customUpload(file, insertFn) {
        self.uploadEditorImage(file, insertFn);
      }
    };
  },
  beforeDestroy() {
    this.destroyEditor();
  },
  mounted() {
    this.load();
  },
  methods: {
    formatTime(t) {
      if (!t) return '—';
      const s = String(t).replace('T', ' ');
      return s.length >= 19 ? s.slice(0, 19) : s;
    },
    resolveMediaUrl(storedPath) {
      if (!storedPath) return '';
      const s = String(storedPath).trim();
      if (/^https?:\/\//i.test(s)) return s;
      const base = resolveApiBaseUrl().replace(/\/$/, '');
      let relative = s.replace(/^\/+/, '');
      if (relative.startsWith('api/files/serve/')) {
        relative = relative.substring('api/files/serve/'.length);
      }
      return `${base}/files/serve/${relative}`;
    },
    onFilterChange() {
      this.page = 1;
      this.load();
    },
    load() {
      this.loading = true;
      const params = { page: this.page, size: this.size };
      if (this.keyword) params.keyword = this.keyword.trim();
      if (this.statusFilter === 0 || this.statusFilter === 1) params.status = this.statusFilter;
      http
        .get('/admin/articles', { params })
        .then(({ data }) => {
          const raw = data.data != null ? data.data : data;
          this.rows = raw.records || [];
          this.total = raw.total || 0;
        })
        .catch((e) => {
          this.$message.error((e.response && e.response.data && e.response.data.message) || '加载失败');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCreate() {
      this.dlgMode = 'create';
      this.resetFormData();
      this.dlg = true;
      this.$nextTick(() => {
        this.editorReady = true;
      });
    },
    /** 编辑回显：正文内相对路径图片转成可访问绝对 URL */
    rewriteContentMediaForEditor(html) {
      if (!html) return '';
      return String(html).replace(/\b(src|href)=(["'])([^"']+)\2/gi, (full, attr, quote, rawUrl) => {
        const url = String(rawUrl || '').trim();
        if (!url || /^data:/i.test(url) || /^#/.test(url) || /^(mailto|tel|javascript):/i.test(url)) {
          return full;
        }
        if (/^https?:\/\//i.test(url) || /^\/\//.test(url)) {
          return full;
        }
        return `${attr}=${quote}${this.resolveMediaUrl(url)}${quote}`;
      });
    },
    openEdit(row) {
      this.dlgMode = 'edit';
      this.form = {
        id: row.id,
        title: row.title || '',
        content: this.rewriteContentMediaForEditor(row.content || ''),
        coverUrl: row.coverUrl || '',
        videoUrl: row.videoUrl || '',
        sortOrder: row.sortOrder != null ? row.sortOrder : 0,
        status: row.status != null ? row.status : 1
      };
      this.dlg = true;
      this.$nextTick(() => {
        this.editorReady = true;
        if (this.$refs.formRef) this.$refs.formRef.clearValidate();
      });
    },
    onEditorCreated(editor) {
      this.editor = Object.seal(editor);
    },
    destroyEditor() {
      const editor = this.editor;
      if (editor == null) return;
      try {
        editor.destroy();
      } catch (e) {
        /* ignore */
      }
      this.editor = null;
    },
    onDialogClosed() {
      this.destroyEditor();
      this.editorReady = false;
      this.resetFormData();
      if (this.$refs.formRef) this.$refs.formRef.resetFields();
    },
    resetFormData() {
      this.form = {
        id: null,
        title: '',
        content: '',
        coverUrl: '',
        videoUrl: '',
        sortOrder: 0,
        status: 1
      };
    },
    uploadEditorImage(file, insertFn) {
      if (!this.beforeImageUpload(file)) return;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'articles');
      http
        .post('/files/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        })
        .then(({ data }) => {
          const body = data;
          const payload = body && body.data;
          const path = (payload && (payload.path || payload.fileID || payload.url)) || (body && body.path);
          if (!path) {
            this.$message.error((body && body.message) || '图片上传失败');
            return;
          }
          insertFn(this.resolveMediaUrl(path), file.name || 'image', this.resolveMediaUrl(path));
        })
        .catch((e) => {
          this.$message.error((e && e.message) || '图片上传失败');
        });
    },
    beforeImageUpload(file) {
      const ok = file.type.startsWith('image/');
      const size = file.size / 1024 / 1024 < 5;
      if (!ok) this.$message.error('须为图片文件');
      if (!size) this.$message.error('图片不能超过 5MB');
      return ok && size;
    },
    beforeVideoUpload(file) {
      const ok = file.type.startsWith('video/');
      const size = file.size / 1024 / 1024 < 50;
      if (!ok) this.$message.error('请上传视频文件');
      if (!size) this.$message.error('视频不能超过 50MB');
      return ok && size;
    },
    onCoverSuccess(res) {
      const body = res;
      const data = body && body.data;
      const path = (data && (data.path || data.fileID)) || (body && body.path);
      if (path) {
        this.form.coverUrl = path;
        this.$message.success('封面上传成功');
      } else {
        this.$message.error((body && body.message) || '上传失败');
      }
    },
    onVideoSuccess(res) {
      const body = res;
      const data = body && body.data;
      const path = (data && (data.path || data.fileID)) || (body && body.path);
      if (path) {
        this.form.videoUrl = path;
        this.$message.success('视频上传成功');
      } else {
        this.$message.error((body && body.message) || '上传失败');
      }
    },
    onVideoRemove() {
      this.form.videoUrl = '';
    },
    save() {
      this.$refs.formRef.validate((valid) => {
        if (!valid) return;
        if (isHtmlContentEmpty(this.form.content)) {
          this.$message.warning('请输入文章内容');
          return;
        }
        this.saving = true;
        const payload = {
          title: this.form.title.trim(),
          content: this.form.content,
          coverUrl: this.form.coverUrl,
          videoUrl: this.form.videoUrl || null,
          sortOrder: this.form.sortOrder,
          status: this.form.status
        };
        const req =
          this.dlgMode === 'create'
            ? http.post('/admin/articles', payload)
            : http.put(`/admin/articles/${this.form.id}`, payload);
        req
          .then(() => {
            this.$message.success('保存成功');
            this.dlg = false;
            this.load();
          })
          .catch((e) => {
            this.$message.error((e.response && e.response.data && e.response.data.message) || '保存失败');
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
    toggleStatus(row) {
      const next = row.status === 1 ? 0 : 1;
      const label = next === 1 ? '启用' : '禁用';
      this.$confirm(`确定${label}该文章？`, '提示', { type: 'warning' })
        .then(() =>
          http.patch(`/admin/articles/${row.id}/status`, { status: next }).then(() => {
            this.$message.success('已更新');
            this.load();
          })
        )
        .catch(() => {});
    },
    remove(row) {
      this.$confirm('确定删除该文章？删除后不可恢复。', '提示', { type: 'warning' })
        .then(() =>
          http.delete(`/admin/articles/${row.id}`).then(() => {
            this.$message.success('已删除');
            this.load();
          })
        )
        .catch(() => {});
    }
  }
};
</script>

<style scoped>
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.thumb {
  width: 56px;
  height: 56px;
  border-radius: 4px;
}
.cell-empty {
  color: #c0c4cc;
}
.btn-danger {
  color: #f56c6c;
}
.upload-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}
.cover-uploader >>> .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
}
.cover-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 120px;
  height: 120px;
  line-height: 120px;
  text-align: center;
  display: block;
}
.cover-preview {
  width: 120px;
  height: 120px;
  object-fit: cover;
  display: block;
}
.rich-editor {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  width: 100%;
  background: #fff;
}
.rich-toolbar {
  border-bottom: 1px solid #dcdfe6;
}
.rich-body {
  height: 360px;
  overflow-y: hidden;
}
</style>
